import SessionHideEmpty from "@images/empty/session_hide_empty.svg"
import { CardioSelectedIcon } from "@images/svgIcons/cardioIcon"
import { CopyIcon } from "@images/svgIcons/copyIcon"
import { GymSelectedIcon } from "@images/svgIcons/gymIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { WeightliftingSelectedIcon } from "@images/svgIcons/weightliftingIcon"
import { useCurrentBox } from "@modules/box/useBox"
import { BoxSlotSession, BoxSlotTypeOf } from "@modules/boxSlot/boxSlotTypes"
import { BoxSlotSessionContentDetailsFullscreen } from "@modules/boxSlot/components/boxSlotSessionContentDetailsFullscreen"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { ScoreboardCard } from "@modules/scoreboard/components/scoreboardCard"
import { useScoreboardSidebar } from "@modules/scoreboard/hooks/useScoreboardSidebar"
import { ScoreType } from "@modules/scoreboard/scoreTypes"
import { ExerciseDetail } from "@modules/sessionBlock/exercises/components/exerciseDetail"
import { Exercise } from "@modules/sessionBlock/exercises/exerciseTypes"
import { useBlockDuration } from "@modules/sessionBlock/hooks/useBlockDuration"
import { MediaDetails } from "@modules/sessionBlock/media/components/mediaDetails"
import { NoteForAthletesDetails } from "@modules/sessionBlock/notes/components/noteForAthletesDetails"
import { NoteForCoachesDetails } from "@modules/sessionBlock/notes/components/noteForCoachesDetails"
import { SessionBlockType } from "@modules/sessionBlock/sessionBlockTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { EmptyView } from "@modules/ui/components/emptyView"
import { FullscreenModal, FullscreenModalRef } from "@modules/ui/components/fullScreenModal/fullscreenModal"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HUStack } from "@modules/ui/components/layout"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { EditIcon } from "@modules/ui/icons/editIcon"
import dayjs from "dayjs"
import { ExpandIcon } from "lucide-react"
import { MenuItem } from "primereact/menuitem"
import { useMemo, useRef } from "react"
import { useTheme } from "styled-components"

type BoxSlotSessionContentDetailsProps = {
  boxSlot: BoxSlotSession
  onEditSessionContent: () => void
  refreshCalendarEvents: () => void
}

export const BoxSlotSessionContentDetails: React.FC<BoxSlotSessionContentDetailsProps> = ({
  boxSlot,
  onEditSessionContent,
  refreshCalendarEvents,
}) => {
  const { result: box } = useCurrentBox()
  const { programSessionService, boxSlotService } = useServicesContext()
  const theme = useTheme()
  const { hideSidebar } = useSidebar()
  const toast = useToast()
  const confirmPopup = useConfirmPopup()
  const canEditSession = useBoxStaffPermission(BoxStaffPermissions.MANAGE_PROGRAMMING)
  const { navigateToScoreboardView } = useScoreboardSidebar()
  const fullscreenModalRef = useRef<FullscreenModalRef>(null)

  const boxSession = boxSlot.session

  const { getBlockDurationInterval, timedBlocks, timedBlocksTotal } = useBlockDuration(boxSession?.blocks)

  const scoreExercicesRequested: Exercise[] = useMemo(() => {
    const listScoreExercicesRequest = boxSession?.blocks
      .filter(
        (block) => block.type === SessionBlockType.SESSION_EXERCISE && block.content.scoreType !== ScoreType.NO_SCORE,
      )
      .map((block) => block.content as Exercise)
    return listScoreExercicesRequest ?? []
  }, [boxSession])

  if (!boxSession) return

  const onClickOnScoreboard = () => {
    navigateToScoreboardView(boxSession)
  }

  const onSessionCopy = () => {
    try {
      programSessionService.setCopySession(boxSession)

      toast.show({
        severity: "success",
        summary: word("program.session.copy.success"),
      })
    } catch (e: any) {
      toast.show({
        severity: "error",
        summary: word("program.session.copy.error"),
      })
    }
  }

  const onDeleteSessionConfirmationPopup = () => {
    confirmPopup.show({
      title: word("sessionDetailed.deleteSession.popup.title"),
      message: word("boxSessionDetailed.deleteSession.popup.message", {
        sessionName: boxSession.name,
      }),
      accept: async () => {
        boxSession && (await boxSlotService.deleteSession(boxSession.id))
        hideSidebar()
        if (refreshCalendarEvents) refreshCalendarEvents()
      },
      footerProps: {
        align: true,
      },
    })
  }

  const ellipsisItems: MenuItem[] = [
    {
      items: [
        {
          label: word("global.edit"),
          icon: <EditIcon color={theme.neutralColor900} />,
          command: () => {
            onEditSessionContent()
          },
        },
        {
          label: word("program.session.copy.label"),
          icon: <CopyIcon color={theme.neutralColor900} width={20} height={20} />,
          command: () => {
            onSessionCopy()
          },
        },
        {
          label: word("global.delete"),
          icon: <TrashIcon color={theme.neutralColor900} width={20} height={20} />,
          command: () => {
            onDeleteSessionConfirmationPopup()
          },
        },
        {
          label: word("program.session.displayFullScreen"),
          icon: <ExpandIcon color={theme.neutralColor900} width={25} height={25} />,
          command: () => {
            fullscreenModalRef.current?.open()
          },
        },
      ],
    },
  ]

  // PERMISSIONS - Coach & Financial permissions denied
  const canUserSeeSessionContent = true // TODO manage permissions

  return (
    <div className="my-5">
      <FullscreenModal ref={fullscreenModalRef}>
        <BoxSlotSessionContentDetailsFullscreen boxSlot={boxSlot} box={box} />
      </FullscreenModal>

      <div className="flex flex-1 justify-content-end gap-2 mb-2">
        {canEditSession && <EllipsisMenu items={ellipsisItems} />}
      </div>
      {!canUserSeeSessionContent ? (
        <EmptyView
          image={{ src: SessionHideEmpty }}
          title={word("session.empty.title.hideSession")}
          description={word("session.empty.description.hideSession")}
          flexGrow={false}
          className="p-6"
          style={{ backgroundColor: theme.neutralColor100 }}
          button={
            <HUButton
              type="Default"
              size="M"
              colorType="Primary"
              text={word("global.button.wait")}
              onClick={() => hideSidebar()}
            />
          }
          paddingType="In_table"
        />
      ) : (
        <HUStack gap={10}>
          <HUText fontStyle="HM">{boxSession.name}</HUText>
          <HUText fontStyle="BM">{dayjs(boxSession.date).format("LL")}</HUText>
          <div className="flex mt-2">
            {boxSession.gymnasticTag && (
              <div className="flex flex-column align-items-center">
                <GymSelectedIcon color={"black"} className="mr-2 mb-1" width={30} height={30} />
              </div>
            )}
            {boxSession.weightliftingTag && (
              <div className="flex flex-column align-items-center">
                <WeightliftingSelectedIcon color={"black"} className="mr-2 mb-1" width={30} height={30} />
              </div>
            )}
            {boxSession.cardioTag && (
              <div className="flex flex-column align-items-center">
                <CardioSelectedIcon color={"black"} className="mb-1" width={30} height={30} />
              </div>
            )}
          </div>

          {boxSession.description && (
            <HURichTextView
              value={formatTextToRichText(boxSession.description)}
              paragrapheFontStyle="BM"
              className="mt-2"
            />
          )}

          {scoreExercicesRequested.length > 0 && (
            <ScoreboardCard
              onClick={() => onClickOnScoreboard()}
              canUserAccessToScoreboard={true}
              isAuthor={false}
              isAcceptedSharedCoach={false}
              isOwner
            />
          )}

          {canUserSeeSessionContent && (
            <div className="flex flex-column pt-2" style={{ gap: 20 }}>
              <div className="flex flex-row justify-content-between align-items-center">
                <HUText fontStyle="LS" color={theme.neutralColor700} className="uppercase">
                  {`${word("program.planning.create.exercise.block_plural", { count: boxSession.blocks.length })} (${
                    boxSession.blocks.length
                  })`}
                </HUText>

                {timedBlocks.length > 0 && (
                  <div>
                    <HUText
                      fontStyle="LS"
                      color={theme.neutralColor700}
                      className="uppercase border-right-1 border-300 pr-1"
                    >
                      {`${word("sessionForm.blocks.timed")} (${timedBlocks.length})`}
                    </HUText>
                    <HUText fontStyle="LS" color={theme.neutralColor700} className="pl-1">
                      {word("sessionForm.block.totalTime")}
                      <HUText fontStyle="LS" className="pl-1 font-bold">
                        {timedBlocksTotal}
                      </HUText>
                      {word("global.minute.shortLabel", { count: timedBlocksTotal })}
                    </HUText>
                  </div>
                )}
              </div>

              {boxSession.blocks.map((block, index) => (
                <div key={index}>
                  {(block.type === SessionBlockType.SESSION_EXERCISE || block.type === SessionBlockType.COACH_NOTE) &&
                    !!block.content.durationInMin && (
                      <HUText fontStyle="BS" className="flex flex-row align-items-center gap-2 mb-1">
                        <div className="bg-neutral-700" style={{ width: 10, height: 10, borderRadius: 3 }} />
                        {getBlockDurationInterval(index)}
                      </HUText>
                    )}
                  {block.type === SessionBlockType.SESSION_EXERCISE ? (
                    <ExerciseDetail
                      exercise={block.content}
                      parentSessionOrigin={{
                        parentType: BoxSlotTypeOf,
                        boxId: box?.id || "",
                        slotId: boxSlot.id,
                        programmationId: boxSlot.programmationId,
                        sessionId: boxSession.id,
                      }}
                      isCoach={true}
                    />
                  ) : block.type === SessionBlockType.COACH_NOTE ? (
                    <NoteForCoachesDetails key={index} note={block.content} />
                  ) : block.type === SessionBlockType.MEDIAS ? (
                    <MediaDetails key={index} medias={block.content.medias} />
                  ) : (
                    <NoteForAthletesDetails key={index} note={block.content} />
                  )}
                </div>
              ))}
            </div>
          )}
        </HUStack>
      )}
    </div>
  )
}
